/**
 * ActivityLogTab Component
 * Displays a chronological timeline of all activity related to a contact
 */

import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  getContactActivityLog,
  type ActivityLogItem,
} from "../../services/activityLog.service";
import { formatDate } from "../../utils/formatters";
import LoadingSpinner from "../common/LoadingSpinner";

interface ActivityLogTabProps {
  contactId: string;
}

// Icon components for different activity types
const ActivityIcon: React.FC<{ type: ActivityLogItem["type"] }> = ({
  type,
}) => {
  switch (type) {
    case "note":
      return (
        <svg
          className="w-5 h-5 text-blue-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
          />
        </svg>
      );
    case "task_created":
      return (
        <svg
          className="w-5 h-5 text-purple-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
          />
        </svg>
      );
    case "task_resolved":
      return (
        <svg
          className="w-5 h-5 text-green-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      );
    case "interaction":
      return (
        <svg
          className="w-5 h-5 text-indigo-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
          />
        </svg>
      );
    case "event":
      return (
        <svg
          className="w-5 h-5 text-orange-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
      );
    case "message":
      return (
        <svg
          className="w-5 h-5 text-teal-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
          />
        </svg>
      );
    case "tag_added":
    case "tag_removed":
      return (
        <svg
          className="w-5 h-5 text-gray-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
          />
        </svg>
      );
    default:
      return (
        <svg
          className="w-5 h-5 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      );
  }
};

// Get link URL for activity item
const getActivityLink = (item: ActivityLogItem): string | null => {
  if (!item.link) {
    return null;
  }

  switch (item.link.type) {
    case "task":
      return `/support-tasks`; // Could link to specific task when we have task detail page
    case "interaction":
      // there is no /interactions/:id route; the Interactions page opens a record from ?interaction=
      return `/interactions?interaction=${item.link.id}`;
    case "event":
      return `/events/${item.link.id}`;
    default:
      return null;
  }
};

// Get background color for activity type
const getActivityBgColor = (type: ActivityLogItem["type"]): string => {
  switch (type) {
    case "note":
      return "bg-blue-50";
    case "task_created":
      return "bg-purple-50";
    case "task_resolved":
      return "bg-green-50";
    case "interaction":
      return "bg-indigo-50";
    case "event":
      return "bg-orange-50";
    case "message":
      return "bg-teal-50";
    default:
      return "bg-gray-50";
  }
};

const ActivityLogTab: React.FC<ActivityLogTabProps> = ({ contactId }) => {
  const {
    data: activities = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["contact-activity-log", contactId],
    queryFn: () => getContactActivityLog(contactId, 100),
    enabled: !!contactId,
  });

  if (isLoading) {
    return <LoadingSpinner size="md" text="Loading activity..." />;
  }

  if (error) {
    return (
      <div className="text-red-600">
        Error loading activity log. Please try again.
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Activity Log</h2>

      {activities.length === 0 ? (
        <p className="text-gray-500 text-center py-8">
          No activity recorded for this contact yet.
        </p>
      ) : (
        <div className="flow-root">
          <ul className="-mb-8">
            {activities.map((activity, index) => {
              const isLast = index === activities.length - 1;
              const link = getActivityLink(activity);

              return (
                <li key={activity.id}>
                  <div className="relative pb-8">
                    {/* Timeline connector */}
                    {!isLast && (
                      <span
                        className="absolute top-5 left-5 -ml-px h-full w-0.5 bg-gray-200"
                        aria-hidden="true"
                      />
                    )}

                    <div className="relative flex items-start space-x-3">
                      {/* Icon */}
                      <div
                        className={`relative flex h-10 w-10 items-center justify-center rounded-full ${getActivityBgColor(activity.type)}`}
                      >
                        <ActivityIcon type={activity.type} />
                      </div>

                      {/* Content */}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between">
                          <div>
                            {link ? (
                              <Link
                                to={link}
                                className="text-sm font-medium text-gray-900 hover:text-blue-600"
                              >
                                {activity.title}
                              </Link>
                            ) : (
                              <span className="text-sm font-medium text-gray-900">
                                {activity.title}
                              </span>
                            )}
                          </div>
                          <time className="flex-shrink-0 text-xs text-gray-500">
                            {formatDate(activity.timestamp)}
                          </time>
                        </div>

                        {activity.description && (
                          <p className="mt-1 text-sm text-gray-600 line-clamp-2">
                            {activity.description}
                          </p>
                        )}

                        {activity.user && (
                          <p className="mt-1 text-xs text-gray-500">
                            by {activity.user.name}
                          </p>
                        )}

                        {/* Show tags if present */}
                        {activity.metadata?.tags &&
                          activity.metadata.tags.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1">
                              {activity.metadata.tags.map((tag: string) => (
                                <span
                                  key={tag}
                                  className="px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded-full"
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ActivityLogTab;
